import { useEffect, useState } from "react";
import { Loader2, KeyRound, PlusCircle } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import api, { formatApiError } from "../lib/api";

export function UserDetailDialog({ userId, open, onOpenChange, onChanged }) {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  const [resetting, setResetting] = useState(false);
  const [adjustments, setAdjustments] = useState({});
  const [adjusting, setAdjusting] = useState(null);

  const load = () => {
    if (!userId) return;
    setLoading(true);
    api
      .get(`/superadmin/users/${userId}`)
      .then(({ data }) => setDetail(data))
      .catch((err) => toast.error(formatApiError(err.response?.data?.detail)))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (open) load();
    else {
      setDetail(null);
      setNewPassword("");
      setAdjustments({});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, userId]);

  const resetPassword = async () => {
    if (newPassword.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    setResetting(true);
    try {
      await api.post(`/superadmin/users/${userId}/reset-password`, { new_password: newPassword });
      toast.success("Password reset successfully");
      setNewPassword("");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setResetting(false);
    }
  };

  const adjustCredits = async (workspaceId) => {
    const form = adjustments[workspaceId] || {};
    const credits_delta = parseInt(form.credits_delta || "0", 10) || 0;
    const max_jobs_delta = parseInt(form.max_jobs_delta || "0", 10) || 0;
    if (!credits_delta && !max_jobs_delta) {
      toast.error("Enter a non-zero credit or job quota change");
      return;
    }
    setAdjusting(workspaceId);
    try {
      await api.post(`/superadmin/workspaces/${workspaceId}/adjust-credits`, {
        credits_delta,
        max_jobs_delta,
        note: form.note || "",
      });
      toast.success("Workspace credits updated");
      setAdjustments((a) => ({ ...a, [workspaceId]: { credits_delta: "", max_jobs_delta: "", note: "" } }));
      load();
      onChanged?.();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setAdjusting(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[85vh] max-w-2xl overflow-y-auto" data-testid="user-detail-dialog">
        <DialogHeader>
          <DialogTitle>{detail?.user?.name || "User details"}</DialogTitle>
        </DialogHeader>

        {loading && (
          <div className="flex items-center justify-center py-10" data-testid="user-detail-loading">
            <Loader2 className="h-6 w-6 animate-spin text-primary" />
          </div>
        )}

        {detail && !loading && (
          <div className="space-y-6">
            <div className="grid grid-cols-2 gap-3 text-sm">
              <Info label="Email" value={detail.user.email} />
              <Info label="Account Created" value={new Date(detail.user.created_at).toLocaleString()} />
              <Info label="Role" value={detail.user.is_superadmin ? "Superadmin" : "User"} />
              <Info label="Email Verified" value={detail.user.email_verified ? "Yes" : "No"} />
            </div>

            <div className="space-y-3">
              <h3 className="text-sm font-semibold text-muted-foreground">Reset Password</h3>
              <div className="flex gap-2">
                <Input
                  type="password"
                  placeholder="New password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  data-testid="admin-reset-password-input"
                />
                <Button onClick={resetPassword} disabled={resetting} data-testid="admin-reset-password-btn">
                  {resetting ? <Loader2 className="h-4 w-4 animate-spin" /> : <KeyRound className="h-4 w-4" />}
                </Button>
              </div>
            </div>

            <div className="space-y-3">
              <h3 className="text-sm font-semibold text-muted-foreground">Workspaces &amp; Credit Adjustment</h3>
              {detail.workspaces.length === 0 ? (
                <p className="text-sm text-muted-foreground">No workspaces.</p>
              ) : (
                detail.workspaces.map((w) => (
                  <div key={w.id} className="rounded-xl border border-border p-4" data-testid={`admin-workspace-card-${w.id}`}>
                    <div className="flex items-center justify-between text-sm">
                      <span className="font-medium">{w.name}</span>
                      <span className="text-xs text-muted-foreground capitalize">{w.role}</span>
                    </div>
                    <div className="mt-1 text-xs text-muted-foreground">
                      Credits: <span className="font-mono">{w.credits_balance}</span> · Job quota:{" "}
                      <span className="font-mono">{w.max_jobs}</span>
                    </div>
                    <div className="mt-3 grid grid-cols-3 gap-2">
                      <Input
                        placeholder="+/- credits"
                        value={adjustments[w.id]?.credits_delta || ""}
                        onChange={(e) => setAdjustments((a) => ({ ...a, [w.id]: { ...a[w.id], credits_delta: e.target.value } }))}
                        data-testid={`admin-credits-delta-input-${w.id}`}
                      />
                      <Input
                        placeholder="+/- job quota"
                        value={adjustments[w.id]?.max_jobs_delta || ""}
                        onChange={(e) => setAdjustments((a) => ({ ...a, [w.id]: { ...a[w.id], max_jobs_delta: e.target.value } }))}
                        data-testid={`admin-jobs-delta-input-${w.id}`}
                      />
                      <Input
                        placeholder="Note (reason)"
                        value={adjustments[w.id]?.note || ""}
                        onChange={(e) => setAdjustments((a) => ({ ...a, [w.id]: { ...a[w.id], note: e.target.value } }))}
                        data-testid={`admin-note-input-${w.id}`}
                      />
                    </div>
                    <Button
                      size="sm"
                      className="mt-2"
                      onClick={() => adjustCredits(w.id)}
                      disabled={adjusting === w.id}
                      data-testid={`admin-apply-adjustment-btn-${w.id}`}
                    >
                      {adjusting === w.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <PlusCircle className="h-4 w-4" />}
                      <span className="ml-1.5">Apply</span>
                    </Button>
                  </div>
                ))
              )}
            </div>

            <div className="space-y-3">
              <h3 className="text-sm font-semibold text-muted-foreground">Credit Purchase History</h3>
              {detail.purchase_history.length === 0 ? (
                <p className="text-sm text-muted-foreground">No purchases or adjustments yet.</p>
              ) : (
                <div className="overflow-x-auto rounded-xl border border-border">
                  <table className="w-full text-sm" data-testid="admin-purchase-history-table">
                    <thead className="bg-muted/50 text-left text-xs uppercase tracking-wide text-muted-foreground">
                      <tr>
                        <th className="px-3 py-2">Workspace</th>
                        <th className="px-3 py-2">Credits</th>
                        <th className="px-3 py-2">Source</th>
                        <th className="px-3 py-2">Status</th>
                        <th className="px-3 py-2">Date</th>
                      </tr>
                    </thead>
                    <tbody>
                      {detail.purchase_history.map((p, i) => (
                        <tr key={i} className="border-t border-border/60">
                          <td className="px-3 py-2">{p.workspace_name}</td>
                          <td className="px-3 py-2 font-mono">{p.credits_added > 0 ? `+${p.credits_added}` : p.credits_added}</td>
                          <td className="px-3 py-2 capitalize">{p.source}</td>
                          <td className="px-3 py-2 capitalize">{p.status}</td>
                          <td className="px-3 py-2 text-xs text-muted-foreground">{new Date(p.created_at).toLocaleString()}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function Info({ label, value }) {
  return (
    <div>
      <div className="text-xs uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className="mt-0.5 font-medium">{value}</div>
    </div>
  );
}
